import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, IconButton, MenuItem, Switch, TextField, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { getTrackerRulesApi, updateTrackerRulesApi } from "src/DAL/coachWorkspace/coachWorkspace";
import { CW, ErrorBox, InfoHint, Loading, PageTitle, Panel } from "./cwUi";

/**
 * P1.8 / P5.4 — admin "Check-in rules" for one journey.
 * Late submit + day change are per journey (also on Add/Edit Tracker →
 * Check-in Configuration). Check-in TIME stays here too.
 * Path /trackers/:id/check-in-rules.
 */

const ZONES = [
  "Europe/Dublin",
  "Europe/London",
  "Europe/Paris",
  "Asia/Karachi",
  "Asia/Dubai",
  "America/New_York",
  "America/Chicago",
  "America/Los_Angeles",
  "Australia/Sydney",
  "UTC",
];

function RuleRow({ title, hint, children }) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "space-between",
        flexDirection: { xs: "column", sm: "row" },
        py: 2,
        borderTop: `1px solid ${CW.line}`,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: CW.ink }}>{title}</Typography>
        <Typography sx={{ fontSize: 13, color: CW.muted, mt: 0.25 }}>{hint}</Typography>
      </Box>
      <Box sx={{ flexShrink: 0 }}>{children}</Box>
    </Box>
  );
}

export default function CheckInRulesPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [rules, setRules] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError("");
    const result = await getTrackerRulesApi(id);
    if (result?.code === 200) {
      const r = result.rules || result;
      setRules(r);
      setForm({
        allowMissedCheckIns: Boolean(r.allowMissedCheckIns),
        allowCheckInDayChange: Boolean(r.allowCheckInDayChange),
        checkInTime: r.checkInTime || "",
        checkInTimeZone: r.checkInTimeZone || "Europe/Dublin",
      });
    } else setError(result?.message || "Could not load the rules");
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    const body = {};
    if (form.allowMissedCheckIns !== Boolean(rules.allowMissedCheckIns)) {
      body.allowMissedCheckIns = form.allowMissedCheckIns;
    }
    if (form.allowCheckInDayChange !== Boolean(rules.allowCheckInDayChange)) {
      body.allowCheckInDayChange = form.allowCheckInDayChange;
    }
    if (form.checkInTime !== (rules.checkInTime || "")) body.checkInTime = form.checkInTime;
    if (form.checkInTimeZone !== (rules.checkInTimeZone || "Europe/Dublin")) {
      body.checkInTimeZone = form.checkInTimeZone;
    }
    const result = await updateTrackerRulesApi(id, body);
    setBusy(false);
    if (result?.code === 200) {
      enqueueSnackbar(result.message || "Check-in rules saved", { variant: "success" });
      load();
    } else enqueueSnackbar(result?.message || "Could not save", { variant: "error" });
  };

  if (error && !rules) return <ErrorBox message={error} onRetry={load} />;
  if (!rules || !form) return <Loading />;

  const changed =
    form.allowMissedCheckIns !== Boolean(rules.allowMissedCheckIns) ||
    form.allowCheckInDayChange !== Boolean(rules.allowCheckInDayChange) ||
    form.checkInTime !== (rules.checkInTime || "") ||
    form.checkInTimeZone !== (rules.checkInTimeZone || "Europe/Dublin");

  const timeChanged =
    form.checkInTime !== (rules.checkInTime || "") ||
    form.checkInTimeZone !== (rules.checkInTimeZone || "Europe/Dublin");
  const missedChanged =
    form.allowMissedCheckIns !== Boolean(rules.allowMissedCheckIns);

  return (
    <div className="container-fluid">
      <Box sx={{ maxWidth: 900, mx: "auto", pb: 6 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <IconButton onClick={() => navigate(`/trackers/${id}`)} aria-label="Back to journey">
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <PageTitle>
              Check-in rules
              <InfoHint id="rules.page" />
            </PageTitle>
            <Typography sx={{ fontSize: 13.5, color: CW.muted }}>
              {rules.trackerName} · {rules.cadenceLabel || "No repeating check-in"}
            </Typography>
          </Box>
        </Box>

        <Panel>
          <RuleRow
            title="Members can submit a missed check-in late"
            hint={
              form.allowMissedCheckIns
                ? "On: a missed check-in can still be filled after its window closes."
                : "Off: when the next check-in opens, the previous one closes."
            }
          >
            <Switch
              checked={form.allowMissedCheckIns}
              onChange={(e) => setForm({ ...form, allowMissedCheckIns: e.target.checked })}
              inputProps={{ "aria-label": "Allow late submit" }}
            />
          </RuleRow>
          <RuleRow
            title="Coaches can change a client's check-in day"
            hint={
              !rules.dayChangeSupported
                ? "Day change is only available on weekly journeys."
                : form.allowCheckInDayChange
                  ? "On: coaches see Change day (preview first; past check-ins keep their dates)."
                  : "Off: every client checks in on this journey's day."
            }
          >
            <Switch
              checked={form.allowCheckInDayChange}
              disabled={!rules.dayChangeSupported}
              onChange={(e) => setForm({ ...form, allowCheckInDayChange: e.target.checked })}
              inputProps={{ "aria-label": "Allow day change" }}
            />
          </RuleRow>
          <RuleRow
            title="Check-in opens at"
            hint={
              form.checkInTime
                ? `Each check-in opens at ${form.checkInTime} (${form.checkInTimeZone}) on its day and closes at the same time when the next opens.`
                : "Empty: check-ins open at the start of the day (as today)."
            }
          >
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <TextField
                size="small"
                type="time"
                label="Time"
                value={form.checkInTime}
                onChange={(e) => setForm({ ...form, checkInTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 130 }}
              />
              <TextField
                select
                size="small"
                label="Time zone"
                value={form.checkInTimeZone}
                onChange={(e) => setForm({ ...form, checkInTimeZone: e.target.value })}
                sx={{ width: 190 }}
              >
                {(ZONES.includes(form.checkInTimeZone) ? ZONES : [form.checkInTimeZone, ...ZONES]).map((z) => (
                  <MenuItem key={z} value={z}>
                    {z}
                  </MenuItem>
                ))}
              </TextField>
              {form.checkInTime ? (
                <button
                  type="button"
                  className="small-contained-outlined-button"
                  onClick={() => setForm({ ...form, checkInTime: "" })}
                >
                  Clear
                </button>
              ) : null}
            </Box>
          </RuleRow>
          {timeChanged ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              Changing the opening time moves every member&apos;s open and upcoming check-in windows on this journey. Submitted check-ins are not changed.
            </Alert>
          ) : null}
          {missedChanged ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              Changing late submit updates open and missed check-ins on this journey.
            </Alert>
          ) : null}
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 2, flexWrap: "wrap" }}>
            <button type="button" className="small-contained-outlined-button" disabled={busy || !changed} onClick={load}>
              Reset
            </button>
            <button type="button" className="small-contained-button" disabled={busy || !changed} onClick={save}>
              Save rules
            </button>
          </Box>
        </Panel>

        <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
          <button
            type="button"
            className="small-contained-outlined-button"
            onClick={() => navigate(`/trackers/edit/${id}`)}
          >
            Edit journey
          </button>
          <button type="button" className="small-contained-outlined-button" onClick={() => navigate(`/check-ins/planner?trackerId=${id}`)}>
            Open check-in planner
          </button>
          <button type="button" className="small-contained-outlined-button" onClick={() => navigate(`/check-ins/${id}/questions`)}>
            Check-in questions
          </button>
        </Box>
      </Box>
    </div>
  );
}
